import { useMemo, useRef, useState } from 'react';
import { Check, Copy, Download, Loader2, Plus, Trash2, Upload, UploadCloud } from 'lucide-react';
import { uploadMedia } from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { StoryEditor, type StoryEditorHandle, type StoryShow, type StoryValue } from './StoryEditor';
import { STORY_TEMPLATES, type StoryVariant } from './stories';
import { clampToCover, coverTransform, exportExt, type ExportFormat } from './photoSlot';
import { FormatToggle } from './FormatToggle';

const CARD_MEDIA_CATEGORY = 'social_card' as const;
const slugify = (s: string) =>
  (s || 'lineup').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const emptyShow = (): StoryShow => ({ photoSrc: null, transform: null, name: '', artist: '', time: '' });

export interface StoryComposerProps {
  accountSlug?: string;
  className?: string;
}

export function StoryComposer({ accountSlug, className }: StoryComposerProps) {
  const [variant, setVariant] = useState<StoryVariant>('guest');
  const template = useMemo(
    () => STORY_TEMPLATES.find((t) => t.variant === variant) ?? STORY_TEMPLATES[0],
    [variant],
  );

  const [value, setValue] = useState<StoryValue>({ date: '', shows: [emptyShow(), emptyShow()] });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // PNG by default: Instagram re-encodes whatever it is handed, and a lossless source
  // costs one generation of artifacts instead of two on this template's hard edges.
  const [format, setFormat] = useState<ExportFormat>('png');

  const editorRef = useRef<StoryEditorHandle>(null);
  const fileInputs = useRef<(HTMLInputElement | null)[]>([]);
  // Natural photo dimensions keyed by object URL, for the per-show zoom slider.
  const [dims, setDims] = useState<Record<string, { w: number; h: number }>>({});

  const clearSaved = () => {
    setUploadedUrl(null);
    setUploadError(null);
    setCopied(false);
  };

  const shownShows = value.shows.slice(0, template.maxShows);

  const setShow = (i: number, patch: Partial<StoryShow>) => {
    clearSaved();
    setValue((v) => ({ ...v, shows: v.shows.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  };

  const changeVariant = (vr: StoryVariant) => {
    clearSaved();
    setVariant(vr);
    const max = STORY_TEMPLATES.find((t) => t.variant === vr)!.maxShows;
    setValue((v) => ({ ...v, shows: v.shows.slice(0, max) }));
  };

  const addShow = () => {
    clearSaved();
    setValue((v) => ({ ...v, shows: [...v.shows, emptyShow()] }));
  };

  const removeShow = (i: number) => {
    clearSaved();
    setValue((v) => ({ ...v, shows: v.shows.filter((_, idx) => idx !== i) }));
  };

  const handleFile = (i: number, file: File | undefined) => {
    if (!file) return;
    clearSaved();
    const url = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => setDims((d) => ({ ...d, [url]: { w: probe.naturalWidth, h: probe.naturalHeight } }));
    probe.src = url;
    setShow(i, { photoSrc: url, transform: null });
  };

  // Zoom a show's photo around its frame centre (slider), clamped to cover.
  const zoomTo = (i: number, nextScale: number) => {
    const show = value.shows[i];
    const dim = show.photoSrc ? dims[show.photoSrc] : undefined;
    if (!show.transform || !dim) return;
    const frame = template.slots[i].photoFrame;
    const cx = frame.x + frame.width / 2;
    const cy = frame.y + frame.height / 2;
    const t = show.transform;
    const imgPtX = (cx - t.x) / t.scale;
    const imgPtY = (cy - t.y) / t.scale;
    const clamped = clampToCover(cx - imgPtX * nextScale, cy - imgPtY * nextScale, dim.w, dim.h, nextScale, frame);
    setShow(i, { transform: { scale: nextScale, ...clamped } });
  };

  const handleDownload = () => {
    const url = editorRef.current?.toDataURL(format);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(value.date)}-${template.variant}-story.${exportExt(format)}`;
    a.click();
  };

  const handleUpload = async () => {
    const blob = await editorRef.current?.toBlob(format);
    if (!blob) return setUploadError('Could not render the story. Try again.');
    setUploading(true);
    setUploadError(null);
    try {
      const base = `${slugify(value.date)}-${template.variant}-story`;
      const result = await uploadMedia(blob, {
        category: CARD_MEDIA_CATEGORY,
        identifier: base,
        fileName: `${base}.${exportExt(format)}`,
        accountSlug,
      });
      setUploadedUrl(result.url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = async () => {
    if (!uploadedUrl) return;
    try {
      await navigator.clipboard.writeText(uploadedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const canAdd = value.shows.length < template.maxShows;
  const inputClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';

  return (
    <div className={className}>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex justify-center">
          <div className="w-full max-w-[420px]">
            <StoryEditor ref={editorRef} template={template} value={value} onChange={setValue} />
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Layout</label>
            <div className="inline-flex rounded-md border border-input p-0.5">
              {(['guest', 'resident'] as StoryVariant[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => changeVariant(v)}
                  className={`rounded px-3 py-1 text-sm capitalize transition-colors ${
                    variant === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Up to {template.maxShows} shows. Drag a photo to reposition; use its zoom slider.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">Date</label>
            <input
              type="text"
              value={value.date}
              onChange={(e) => {
                clearSaved();
                setValue((v) => ({ ...v, date: e.target.value }));
              }}
              placeholder="e.g. WED JAN 11"
              className={inputClass}
            />
          </div>

          <div className="space-y-4">
            {shownShows.map((show, i) => (
              <div key={i} className="space-y-2 rounded-md border border-input p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Show {i + 1}</span>
                  {value.shows.length > 1 && (
                    <button type="button" onClick={() => removeShow(i)} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <input
                  ref={(el) => {
                    fileInputs.current[i] = el;
                  }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(i, e.target.files?.[0])}
                />
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => fileInputs.current[i]?.click()}>
                  <Upload className="mr-2 h-4 w-4" />
                  {show.photoSrc ? 'Replace photo' : 'Upload photo'}
                </Button>
                {show.photoSrc && show.transform && show.photoSrc in dims && (
                  (() => {
                    const dim = dims[show.photoSrc!];
                    const cover = coverTransform(dim.w, dim.h, template.slots[i].photoFrame).scale;
                    return (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Zoom</span>
                        <input
                          type="range"
                          min={cover}
                          max={cover * 4}
                          step={0.01}
                          value={show.transform.scale}
                          onChange={(e) => zoomTo(i, parseFloat(e.target.value))}
                          className="flex-1"
                        />
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => setShow(i, { transform: coverTransform(dim.w, dim.h, template.slots[i].photoFrame) })}
                        >
                          Reset
                        </button>
                      </div>
                    );
                  })()
                )}
                <input
                  type="text"
                  value={show.name}
                  onChange={(e) => setShow(i, { name: e.target.value })}
                  placeholder={variant === 'resident' ? 'Show name' : 'Artist name'}
                  className={inputClass}
                />
                {variant === 'resident' && (
                  <input
                    type="text"
                    value={show.artist ?? ''}
                    onChange={(e) => setShow(i, { artist: e.target.value })}
                    placeholder="Artist name"
                    className={inputClass}
                  />
                )}
                <input
                  type="text"
                  value={show.time}
                  onChange={(e) => setShow(i, { time: e.target.value })}
                  placeholder="Set time (e.g. 20:00 - 21:00)"
                  className={inputClass}
                />
              </div>
            ))}

            {canAdd && (
              <Button type="button" variant="outline" size="sm" className="w-full" onClick={addShow}>
                <Plus className="mr-2 h-4 w-4" />
                Add show
              </Button>
            )}
          </div>

          <div className="space-y-2 border-t pt-5">
            <FormatToggle value={format} onChange={setFormat} />
            <Button type="button" variant="outline" className="w-full" onClick={handleDownload}>
              <Download className="mr-2 h-4 w-4" />
              Download {format === 'jpeg' ? 'JPG' : 'PNG'}
            </Button>
            <Button type="button" className="w-full" onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
              {uploading ? 'Saving…' : 'Save to storage'}
            </Button>
            {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
            {uploadedUrl && (
              <div className="rounded-md border border-input bg-muted/40 p-2">
                <p className="mb-1 text-xs font-medium text-muted-foreground">Saved to storage</p>
                <div className="flex items-center gap-2">
                  <a href={uploadedUrl} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-xs text-primary hover:underline" title={uploadedUrl}>
                    {uploadedUrl}
                  </a>
                  <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
